import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '中立可信',
    desc: '纯免费信息撮合平台，不参与任何交易环节，始终保持中立立场，为供需双方提供公平透明的对接环境。',
  },
  {
    icon: 'ri-leaf-line',
    title: '绿色使命',
    desc: '以助力企业绿色低碳转型为使命，推动可再生能源证书的高效流通，为实现"双碳"目标贡献力量。',
  },
  {
    icon: 'ri-service-line',
    title: '服务至上',
    desc: '从信息发布到对接撮合，全程免费提供技术支持与咨询服务，让每一个用户都能获得专业、高效的服务体验。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '创新驱动',
    desc: '持续投入能耗管理系统等核心产品研发，以数字化手段帮助企业发现节能潜力，实现降本增效。',
  },
];

const milestones = [
  { year: '2024', title: '平台立项', desc: '绿证通项目启动，聚焦绿证供需信息不对称痛点' },
  { year: '2025', title: '产品上线', desc: '绿证供需撮合平台正式上线，首批服务企业超100家' },
  { year: '2026 Q1', title: '能耗系统发布', desc: '能耗管理系统专题页上线，为企业提供能源数字化管理方案' },
  { year: '2026 Q2', title: '持续迭代', desc: '个人中心、行业资讯、资讯详情等功能陆续上线，平台生态持续完善' },
];

const stats = [
  { value: '500+', label: '服务企业' },
  { value: '10,000+', label: '绿证信息' },
  { value: '30+', label: '覆盖省份' },
  { value: '98%', label: '用户满意度' },
];

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main className="w-full bg-background-50">
        {/* Hero */}
        <section className="relative w-full min-h-[480px] md:min-h-[560px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/about-hero.jpg"
              alt="关于我们"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center">
              <span className="inline-block px-3 py-1 bg-primary-500/80 text-background-50 text-xs font-medium rounded-full tracking-wide mb-5">
                关于绿证通
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight tracking-tight">
                让绿色价值
                <br />
                <span className="text-primary-300">自由流动</span>
              </h1>
              <p className="text-background-100/80 text-base md:text-lg mt-5 leading-relaxed max-w-2xl mx-auto">
                绿证通是国内领先的绿证供需信息撮合平台，致力于打破可再生能源证书交易的信息壁垒，
                让每一张绿证都能找到需要它的企业。
              </p>
            </div>
          </div>
        </section>

        {/* Company Story */}
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-primary-600 text-sm font-medium font-label tracking-wide uppercase">
                  我们的故事
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3 leading-tight">
                  从信息不对称
                  <br />
                  到高效对接
                </h2>
                <div className="space-y-4 mt-6">
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    在"双碳"目标的大背景下，越来越多的企业开始关注绿色电力消费和碳减排。然而，
                    绿证交易市场长期存在信息不对称问题：发电企业手握绿证却不知如何找到买家，
                    需求企业急需绿证却难以找到合适的供应方。
                  </p>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    绿证通应运而生。我们打造了一个纯免费的信息撮合平台，不参与任何交易环节，
                    仅做供需信息展示与对接服务。通过数字化手段，让绿证的流转更加透明、高效。
                  </p>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    同时，我们深知企业绿色转型的根本在于能源管理的数字化升级。因此，
                    我们同步研发了<strong>能耗管理系统</strong>，帮助企业从能耗监测、能效分析到碳排核算，
                    实现全链路能源管理，真正让每一度电都创造价值。
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-100 rounded-xl p-6 text-center"
                  >
                    <p className="text-3xl md:text-4xl font-heading font-bold text-primary-600">
                      {stat.value}
                    </p>
                    <p className="text-foreground-500 text-sm mt-2">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-accent-600 text-sm font-medium font-label tracking-wide uppercase">
                核心价值观
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3">
                我们坚守的原则
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="bg-background-50 rounded-xl p-6 md:p-7 border border-background-200/70 hover:border-primary-200 transition-colors duration-200"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-5">
                    <i className={`${v.icon} text-xl text-primary-600`} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2.5">
                    {v.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-primary-600 text-sm font-medium font-label tracking-wide uppercase">
                发展历程
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3">
                每一步，都在向前
              </h2>
            </div>
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-background-300" />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {milestones.map((m, idx) => (
                  <div key={idx} className="relative text-center">
                    <div className="hidden md:flex absolute top-8 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50 z-10" />
                    <div className="md:pt-16">
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-bold rounded-full mb-3">
                        {m.year}
                      </span>
                      <h4 className="font-heading text-base font-bold text-foreground-900 mb-2">
                        {m.title}
                      </h4>
                      <p className="text-foreground-500 text-sm leading-relaxed">
                        {m.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Platform Highlights */}
        <section className="w-full py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-accent-600 text-sm font-medium font-label tracking-wide uppercase">
                平台优势
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3">
                为什么选择绿证通
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-vip-crown-line',
                  title: '完全免费',
                  desc: '平台不收取任何撮合费用、会员费用或交易佣金，所有基础服务对供需双方完全免费开放。',
                },
                {
                  icon: 'ri-user-search-line',
                  title: '精准匹配',
                  desc: '多维度筛选体系（类型、年份、地区、数量），帮助用户快速找到最匹配的供需对象。',
                },
                {
                  icon: 'ri-shield-user-line',
                  title: '信息透明',
                  desc: '供需信息完整展示，价格、数量、有效期一目了然，降低交易前的沟通成本。',
                },
                {
                  icon: 'ri-24-hours-line',
                  title: '实时更新',
                  desc: '供给与需求信息实时同步，市场动态一手掌握，不错过任何商机。',
                },
                {
                  icon: 'ri-file-shield-line',
                  title: '风险提示',
                  desc: '全站常驻交易风险提示，提醒用户自行核验信息真实性，保障交易安全。',
                },
                {
                  icon: 'ri-dashboard-line',
                  title: '能耗管理',
                  desc: '配套能耗管理系统，帮助企业从能耗监测到碳排核算，实现全链路能源管理。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 bg-background-50 rounded-xl p-6"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-lg text-primary-600`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
                  有任何问题？
                  <br />
                  <span className="text-primary-600">我们随时为您解答</span>
                </h2>
                <p className="text-foreground-500 text-base mt-4 leading-relaxed">
                  无论是平台使用问题、绿证交易咨询，还是能耗管理系统的需求，
                  我们的专业团队都将为您提供一对一的解答与支持。
                </p>
                <div className="mt-8 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className="ri-phone-line text-primary-600" />
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">客服热线</p>
                      <p className="text-foreground-900 text-sm font-medium">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-primary-600" />
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">联系邮箱</p>
                      <p className="text-foreground-900 text-sm font-medium">info@firmai.cn</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-2xl p-8 md:p-10">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">
                  了解更多产品
                </h3>
                <p className="text-foreground-500 text-sm mb-6">
                  除了绿证供需撮合，我们还提供企业级能耗管理系统，助力您的绿色转型之路。
                </p>
                <div className="space-y-3">
                  <a
                    href="https://ems.firmai.cn/web"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                      <i className="ri-flashlight-line text-xl text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-foreground-900 text-sm font-semibold">能耗管理系统</h4>
                      <p className="text-foreground-400 text-xs mt-0.5">实时监测 · 智能分析 · 碳排核算</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 transition-colors" />
                  </a>
                  <Link
                    to="/news"
                    className="flex items-center gap-4 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-200 transition-colors">
                      <i className="ri-newspaper-line text-xl text-secondary-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-foreground-900 text-sm font-semibold">行业资讯</h4>
                      <p className="text-foreground-400 text-xs mt-0.5">绿证政策 · 双碳动态 · 节能案例</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 transition-colors" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full py-14 md:py-20 bg-foreground-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              加入绿色未来
            </h2>
            <p className="text-foreground-400 text-base mt-4 max-w-xl mx-auto">
              无论您是绿证供应方还是需求方，绿证通都将是您最值得信赖的合作伙伴。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <Link
                to="/supply"
                className="px-8 py-3.5 bg-background-50 text-foreground-900 text-sm font-semibold rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览绿证供给
              </Link>
              <Link
                to="/demand"
                className="px-8 py-3.5 border border-background-50/30 text-background-50 text-sm font-semibold rounded-md hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看采购需求
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
